import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { TutorPage } from '../lib/tutorNotebook.ts';
import { mayCollapseNote, notebookQuestion } from '../lib/tutorNotebook.ts';
import { formatEquation, applyOperation } from '../../shared/algebra.ts';
import { WrittenBoard } from './WrittenBoard.tsx';
import { AnimatedBoard } from './AnimatedBoard.tsx';
import { replyAlignment } from '../lib/replyAlignment.ts';
import { buildAlgebraTeachingPlan } from '../lib/algebraTeachingPlan.ts';
import { demonstrationProgress } from '../lib/workshopFlow.ts';
import type { NarratedWord } from '../lib/narratedOperation.ts';
import './tutor-notebook.css';
import { ExplanationTimeline } from './ExplanationTimeline.tsx';
type Props = {
  page: TutorPage;
  pages: TutorPage[];
  style: 'written' | 'illustration';
  playing: boolean;
  busy: boolean;
  position: number;
  duration: number;
  words: readonly NarratedWord[];
  playbackId: number;
  reduced: boolean;
  onSelect: (id: string) => void;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (at: number) => void;
  onClose: () => void;
  onDelete: () => void;
  onAsk: (text: string) => void;
  onWhy?: () => void;
  representationControl?: ReactNode;
};
export function TutorNotebook(p: Props) {
  const invitation = notebookQuestion(p.page.text);
  const [pinned, setPinned] = useState(false),
    [autoReturn, setAutoReturn] = useState(false),
    [hovered, setHovered] = useState(false),
    [focused, setFocused] = useState(false),
    [visible, setVisible] = useState(!document.hidden),
    [remaining, setRemaining] = useState<number | null>(null),
    [question, setQuestion] = useState('');
  const wasPlaying = useRef(false);
  const management = useRef<HTMLDetailsElement>(null);
  const canvasHeading = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (!window.matchMedia('(max-width: 760px)').matches) return;
    const frame = requestAnimationFrame(() => {
      const heading = canvasHeading.current;
      if (!heading) return;
      const bounds = heading.getBoundingClientRect();
      // Reopening or starting playback reveals this notebook, without changing stage.
      if (bounds.top < 0 || bounds.top > window.innerHeight * 0.35)
        heading.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [p.page.id, p.playbackId, p.playing]);
  const interacting = hovered || focused;
  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  useEffect(() => {
    if (management.current) management.current.open = false;
    setRemaining(null);
    setPinned(false);
    setQuestion('');
    wasPlaying.current = false;
  }, [p.page.id]);
  useEffect(() => {
    if (p.playing || !autoReturn) setRemaining(null);
    else if (
      autoReturn &&
      wasPlaying.current &&
      p.duration > 0 &&
      p.position >= p.duration - 100
    )
      setRemaining(8);
    wasPlaying.current = p.playing;
  }, [p.playing, p.duration, p.position, autoReturn]);
  useEffect(() => {
    if (
      remaining === null ||
      !autoReturn ||
      pinned ||
      interacting ||
      p.playing ||
      p.busy ||
      !visible
    )
      return;
    const timer = setTimeout(() => {
      if (!document.hidden) setRemaining((n) => (n === null ? null : n - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [remaining, autoReturn, pinned, interacting, p.playing, p.busy, visible]);
  useEffect(() => {
    if (
      autoReturn &&
      visible &&
      mayCollapseNote({
        remaining,
        pinned,
        interacting,
        playing: p.playing,
        busy: p.busy,
      })
    )
      p.onClose();
  }, [remaining, autoReturn, visible, pinned, interacting, p.playing, p.busy]);
  const plan = buildAlgebraTeachingPlan(
    p.page.equation,
    p.page.operation,
    p.words,
    p.duration,
    p.page.candidate,
  );
  const step = plan.steps.filter((s) => p.position >= s.startMs).at(-1);
  const amount = demonstrationProgress(
    p.words.length ? step : undefined,
    p.position,
    p.page.operation?.kind === 'divide',
    p.busy,
    p.reduced,
  );
  const equation =
    p.page.operation && amount >= 0.8
      ? applyOperation(p.page.equation, p.page.operation)
      : p.page.equation;
  const cue = replyAlignment({
    text: p.page.text,
    equation,
    words: p.words,
    positionMs: p.position,
  });
  const index = p.pages.findIndex((n) => n.id === p.page.id);
  return (
    <section
      className="tutor-notebook notebook-focused"
      aria-label="Tutor notebook"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
      onWheel={() => setPinned(true)}
      onTouchMove={() => setPinned(true)}
    >
      <header className="notebook-header" ref={canvasHeading}>
        <div>
          <span className="eyebrow">
            Tutor explanation · {formatEquation(p.page.equation)}
          </span>
          <h2>{p.page.title}</h2>
          {autoReturn && <p>Automatic return is on in notebook options.</p>}
        </div>
        <button className="secondary notebook-return" onClick={p.onClose}>
          Back to my work
        </button>
      </header>
      <div className="notebook-playback">
        <button
          className="primary"
          aria-label={
            p.playing
              ? 'Pause explanation'
              : p.busy
                ? 'Preparing audio'
                : 'Play explanation'
          }
          disabled={p.busy}
          onClick={p.playing ? p.onPause : p.onPlay}
        >
          <span className="notebook-label-wide">
            {p.playing
              ? 'Pause explanation'
              : p.busy
                ? 'Preparing audio…'
                : 'Play explanation'}
          </span>
          <span className="notebook-label-short">
            {p.playing ? 'Pause' : p.busy ? 'Loading…' : 'Play'}
          </span>
        </button>
        {p.representationControl}
        <details className="notebook-management" ref={management}>
          <summary aria-label="Pages & options">
            <span className="notebook-label-wide">Pages &amp; options</span>
            <span className="notebook-label-short">Notes</span>
          </summary>
          <div className="notebook-options-panel">
            {p.onWhy && <button className="secondary" onClick={p.onWhy}>Why is this allowed?</button>}
            {p.page.reflection && (
              <details className="notebook-transcript">
                <summary>Your answer</summary>
                <p>{p.page.reflection.prompt}</p>
                <blockquote className="notebook-prose">{p.page.reflection.learnerAnswer}</blockquote>
                <p>Practice conversation · saved with this explanation.</p>
              </details>
            )}
            <p>
              Saved explanations · {index + 1} of {p.pages.length}
            </p>
            <nav
              className="notebook-controls"
              aria-label="Tutor notebook pages"
            >
              <button
                className="secondary"
                disabled={index === 0}
                onClick={() => p.onSelect(p.pages[index - 1].id)}
              >
                Previous page
              </button>
              <label>
                Page
                <select
                  value={p.page.id}
                  onChange={(e) => p.onSelect(e.target.value)}
                >
                  {p.pages.map((n, i) => (
                    <option key={n.id} value={n.id}>
                      {i + 1}. {n.title}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="secondary"
                disabled={index === p.pages.length - 1}
                onClick={() => p.onSelect(p.pages[index + 1].id)}
              >
                Next page
              </button>
            </nav>
            <details className="notebook-transcript">
              <summary>Read the full explanation</summary>
              <p className="notebook-prose">{p.page.text}</p>
              {plan.steps.length > 1 && (
                <ol>
                  {plan.steps.map((s) => (
                    <li key={s.id}>
                      <strong>{s.text}</strong>
                      <p>{s.caption}</p>
                    </li>
                  ))}
                </ol>
              )}
            </details>
            <label className="notebook-auto-return">
              <input
                type="checkbox"
                checked={autoReturn}
                onChange={(e) => {
                  setAutoReturn(e.target.checked);
                  setPinned(false);
                  setRemaining(null);
                }}
              />
              Automatically return to my work eight seconds after playback ends
            </label>
            <footer>
              <span>Explanations are saved in this browser.</span>
              <button className="text-button" onClick={p.onDelete}>
                Delete this page
              </button>
            </footer>
          </div>
        </details>
        {autoReturn && remaining !== null && !pinned && (
          <div className="notebook-countdown" role="status">
            <span>
              {interacting
                ? 'Reading pauses the countdown'
                : `Back to your work in ${Math.max(0, remaining)}s`}
            </span>
            <progress max={8} value={Math.max(0, remaining)} />
            <button
              className="text-button"
              onClick={() => {
                setPinned(true);
                setRemaining(null);
              }}
            >
              Keep this explanation open
            </button>
          </div>
        )}
      </div>
      <ExplanationTimeline
        position={p.position}
        duration={p.duration}
        disabled={p.busy}
        markers={plan.steps.map((s) => ({
          id: s.id,
          startMs: s.startMs,
          label: s.caption,
        }))}
        onSeek={(at) => {
          setPinned(true);
          setRemaining(null);
          p.onSeek(at);
        }}
      />
      <div className="notebook-visual">
        {p.style === 'written' || p.page.candidate !== undefined ? (
          <>
            <WrittenBoard
              equation={p.page.equation}
              operation={p.page.operation}
              candidate={p.page.candidate}
              noteText={p.page.text}
              words={p.words}
              positionMs={p.position}
              durationMs={p.duration}
              speaking={p.playing}
              preparing={p.busy && !p.words.length}
              reduced={p.reduced}
              playbackId={`${p.page.id}:${p.playbackId}`}
              label="Tutor’s saved explanation"
            />
            {!p.page.operation && p.page.candidate === undefined && (
              <p className="notebook-concept-caption">
                {cue.excerpt || p.page.text}
              </p>
            )}
          </>
        ) : (
          <>
            {p.page.operation && amount >= 0.8 && (
              <div
                className="notebook-earlier-state"
                aria-label="Earlier state of this worked example"
              >
                <span>Started with</span>
                <strong>{formatEquation(p.page.equation)}</strong>
                <span aria-hidden="true">→</span>
                <span>Now</span>
                <strong>{formatEquation(equation)}</strong>
              </div>
            )}
            <AnimatedBoard
              equation={equation}
              frame={{
                kind: 'practice',
                phase: 0,
                progress: 1,
                focus: cue.focus,
                pointerFocus: cue.pointerFocus,
                word: cue.word,
                caption:
                  cue.excerpt ||
                  (!p.page.operation && p.page.candidate === undefined
                    ? p.page.text
                    : 'Play the explanation, or open its transcript to read at your pace.'),
                equation: formatEquation(equation),
              }}
              speaking={p.playing}
              reduced={p.reduced}
              operation={p.page.operation}
              operationProgress={amount}
            />
          </>
        )}
      </div>
      <form
        className="notebook-question"
        onSubmit={(e) => {
          e.preventDefault();
          if (question.trim()) p.onAsk(question);
        }}
      >
        <label htmlFor="note-question">{invitation || 'Ask about this explanation'}</label>
        <div>
          <input
            id="note-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={invitation ? 'Share your thinking, or ask a question' : 'Why does this step work?'}
            maxLength={1600}
          />
          <button className="secondary" disabled={!question.trim() || p.busy}>
            Send
          </button>
        </div>
      </form>
    </section>
  );
}
